/**
 * v3.compliance — /v3/compliance/* (RCM change-of-contact fraud scan, RAM
 * escheatment flags, future BSA/AML hooks). Backed by PCBAccountOpeningApi
 * route group planned for 0.1.19.
 *
 * Minimal surface at add-time — only `fraudScan` is wired. Other endpoints
 * will land alongside their consuming modules (per teams-app-wiring §7).
 */
import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface FraudScanRequest {
  cifId: string;
  /** Classification for the triggering edit. `contact` = phone/email/address. */
  changeType: 'contact' | 'identity' | 'ownership' | 'other';
  /** Field keys that changed (CifEditPanel FIELDS[].key shape). */
  changedFields?: ReadonlyArray<string>;
  /** Pre-change snapshot (subset — only fields relevant to the change type). */
  before?: Record<string, string>;
  /** Post-change snapshot. */
  after?: Record<string, string>;
  /** Officer making the change. */
  officerCode?: string;
}

export interface FraudScanResult {
  success: boolean;
  /** FraudAlert taskIds the server opened as a result of this scan. */
  createdTaskIds?: ReadonlyArray<string>;
  /** Optional server-side match detail (insider CIF, matched field, etc.). */
  matches?: ReadonlyArray<{
    matchedField: string;
    matchedValue: string;
    insiderCifId?: string;
    insiderName?: string;
    severity?: 'info' | 'warn' | 'critical';
  }>;
  error?: string;
}

/** One employee in the insider-scan list (names and status only, never customer numbers). */
export interface InsiderScanEmployeeRow {
  displayName: string;
  /** matched | no-customer-record | no-employee-code | error */
  status: string;
  matchedCifCount: number;
  detail?: string | null;
  checkedAtUtc: string;
}

export interface InsiderScanStatus {
  success: boolean;
  scanEnabled: boolean;
  syncEnabled: boolean;
  employeeCodes: string;
  lastSyncUtc?: string | null;
  fingerprints: number;
  counts: Record<string, number>;
  employees: ReadonlyArray<InsiderScanEmployeeRow>;
  error?: string;
}

export interface InsiderScanSyncResult {
  success: boolean;
  ran: boolean;
  reason?: string | null;
  employees: number;
  matched: number;
  noCustomerRecord: number;
  noEmployeeCode: number;
  errors: number;
  fingerprints: number;
  elapsedMs: number;
}

export class ComplianceApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public fraudScan(
    req: FraudScanRequest,
    opts?: RequestOptions,
  ): Promise<FraudScanResult> {
    return this.http.post<FraudScanResult>('/v3/compliance/fraud-scan', {
      ...opts,
      body: req,
    });
  }

  /** SysOps panel: insider-scan switches, last sync and per-employee status. GET /v3/sysops/insider-scan */
  public insiderScanStatus(opts?: RequestOptions): Promise<InsiderScanStatus> {
    return this.http.get<InsiderScanStatus>('/v3/sysops/insider-scan', opts);
  }

  /** SysOps panel: rebuild the employee list now. POST /v3/sysops/insider-scan/sync */
  public insiderScanSync(opts?: RequestOptions): Promise<InsiderScanSyncResult> {
    return this.http.post<InsiderScanSyncResult>('/v3/sysops/insider-scan/sync', { ...opts });
  }
}
